import { useContext, useState } from 'react'
import { Draggable } from '@hello-pangea/dnd'

import { Container, Row as GridRow } from 'js/components/Grid'
import getRowProperties from 'js/page/helpers/getRowProperties'
import AddColumnActions from 'js/page/PageEditor/components/AddColumnActions'
import Column from 'js/page/PageEditor/components/Column'
import { getDefaultColumnWidth } from 'js/page/PageEditor/helpers/getColumnWidth'
import type { PageEditorRowType } from 'js/page/PageEditor/PageEditor.types'
import PageEditorContext from 'js/page/PageEditor/PageEditorContext'

import PageEditorRowActions from './PageEditorRowActions/PageEditorRowActions'
import StyledPageEditorRow from './StyledPageEditorRow'

type PageEditorRowProps = {
    rows: PageEditorRowType[]
    rowIndex: number
    entity: PageEditorRowType
    growIsEnabled: any
}

const PageEditorRow = ({
    rows,
    rowIndex,
    entity,
    growIsEnabled,
}: PageEditorRowProps) => {
    const { isFullWidth, columns, backgroundColor } = entity
    const numberOfColumns = columns.length

    const [showBackgroundColorSetting, setShowBackgroundColorSetting] =
        useState(false)

    const [isFocused, setIsFocused] = useState(false)

    const { setRows, isDragging, widgetEditing } = useContext(PageEditorContext)

    const handleAddColumn = (position: 'left' | 'right') => {
        const newWidth = getDefaultColumnWidth(numberOfColumns + 1)

        const emptyColumn = {
            width: [newWidth],
            widgets: [],
        }

        const newColumns =
            position === 'left'
                ? [emptyColumn, ...columns]
                : [...columns, emptyColumn]

        setRows((prev: any) =>
            prev.map((row: any, idx: number) =>
                idx === rowIndex
                    ? {
                          ...row,
                          columns: newColumns.map((col) => ({
                              ...col,
                              width: [newWidth],
                          })),
                      }
                    : row,
            ),
        )
    }

    const rowBackgroundColor = backgroundColor || '#f5f5f5' // Fallback color is needed in editor to have a bg for dragging en to create variations (--ref-palette-neutral-10)

    const isActive = isFocused || showBackgroundColorSetting

    return (
        <Draggable
            key={`${entity.rowGuid}`} // Do not remove - @hello-pangea/dnd needs this
            draggableId={`${entity.rowGuid}`}
            index={rowIndex}
            isDragDisabled={!!widgetEditing}
        >
            {(provided, snapshot) => (
                <StyledPageEditorRow
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...getRowProperties({
                        rows,
                        row: entity,
                        rowIndex,
                        growIsEnabled,
                    })}
                    $isFocused={!isDragging && !widgetEditing && isActive}
                    $isDragging={snapshot.isDragging}
                    backgroundColor={rowBackgroundColor} // Is passed down to <Section />
                    onMouseEnter={() => setIsFocused(true)}
                    onMouseLeave={() => setIsFocused(false)}
                >
                    <PageEditorRowActions
                        show={
                            !widgetEditing &&
                            (snapshot.isDragging || (isActive && !isDragging))
                        }
                        snapshot={snapshot}
                        provided={provided}
                        rowIndex={rowIndex}
                        numberOfColumns={numberOfColumns}
                        isFullWidth={isFullWidth}
                        backgroundColor={backgroundColor}
                        showBackgroundColorSetting={showBackgroundColorSetting}
                        setShowBackgroundColorSetting={
                            setShowBackgroundColorSetting
                        }
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                    />
                    <Container className="PageRowContainer">
                        {!isDragging && !isFullWidth && numberOfColumns < 4 && (
                            <AddColumnActions
                                className="AddColumnActions"
                                onAddColumn={handleAddColumn}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                            />
                        )}

                        <GridRow
                            $spacing={20}
                            // Grow widget height to column
                            $growContent
                        >
                            {entity.columns.map((column, index) => (
                                <Column
                                    key={`${rowIndex}-${index}`}
                                    rowIndex={rowIndex}
                                    columnIndex={index}
                                    entity={column}
                                    numberOfColumns={numberOfColumns}
                                    rowBackgroundColor={rowBackgroundColor}
                                    showRowBackgroundColorSetting={
                                        showBackgroundColorSetting
                                    }
                                />
                            ))}
                        </GridRow>
                    </Container>
                </StyledPageEditorRow>
            )}
        </Draggable>
    )
}

export default PageEditorRow
